// src/pages/RegisterPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FaBolt,
  FaUserPlus,
  FaShoppingCart,
  FaShieldAlt,
  FaShippingFast,
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaUserCircle,
  FaArrowLeft
} from 'react-icons/fa';
import { GoogleLogin } from '@react-oauth/google';
import RegisterForm from '../components/Auth/RegisterForm';
import '../styles/RegisterPage.css';
import reanimeitorImage from '../Assets/reanimeitor-can.png.png';

const RegisterPage = () => {
  const mobileStatsRef = useRef(null);
  const navigate = useNavigate();

  const API_URL = '/backend';

  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);

  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationUserId, setVerificationUserId] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);

  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const saveLoginDataAndRedirect = (user, token) => {
    if (!user || !token) {
      setError('No se recibieron los datos de sesión correctamente.');
      return;
    }

    localStorage.setItem('userData', JSON.stringify(user));
    localStorage.setItem('userToken', token);
    window.dispatchEvent(new Event('storage'));

    navigate('/account');
  };

  useEffect(() => {
    document.body.classList.add('reanimeitor-register-view');

    document.title = 'Crear cuenta | Reanimeitor Energy Drink';

    let metaDescription = document.querySelector('meta[name="description"]');

    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute(
      'content',
      'Crea tu cuenta en Reanimeitor Energy Drink para comprar packs, consultar tus pedidos y gestionar tus datos de cliente.'
    );

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    canonical.setAttribute('href', 'https://reanimeitor.com/register');

    let robots = document.querySelector('meta[name="robots"]');

    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }

    robots.setAttribute('content', 'index, follow');

    return () => {
      document.body.classList.remove('reanimeitor-register-view');
    };
  }, []);

  useEffect(() => {
    const slider = mobileStatsRef.current;

    if (!slider) {
      return undefined;
    }

    let index = 0;

    const interval = setInterval(() => {
      if (window.innerWidth > 768) {
        return;
      }

      const slides = slider.querySelectorAll('.register-mobile-stat-slide');

      if (!slides.length) {
        return;
      }

      index = (index + 1) % slides.length;

      slider.scrollTo({
        left: slides[index].offsetLeft,
        behavior: 'smooth'
      });
    }, 3200);

    return () => clearInterval(interval);
  }, []);

  const handleGoogleRegisterSuccess = async (credentialResponse) => {
    setError('');
    setGoogleLoading(true);

    try {
      const credential = credentialResponse.credential;

      if (!credential) {
        setError('Google no devolvió una credencial válida.');
        return;
      }

      const res = await axios.post(
        `${API_URL}/google-auth.php`,
        { credential },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        }
      );

      if (res.data.success) {
        saveLoginDataAndRedirect(res.data.user, res.data.token);
        return;
      }

      if (res.data.needsVerification) {
        setVerificationEmail(res.data.email);
        setVerificationUserId(res.data.user_id);
        setShowVerification(true);
        return;
      }

      setError(res.data.error || 'Error al registrarte con Google.');
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Error procesando el registro con Google.'
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('El código debe tener 6 dígitos.');
      return;
    }

    setVerificationLoading(true);
    setError('');

    try {
      const res = await axios.post(
        `${API_URL}/verificar_codigo.php`,
        {
          user_id: verificationUserId,
          codigo: verificationCode
        },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (res.data.success) {
        setVerificationSuccess(true);

        if (res.data.user && res.data.token) {
          localStorage.setItem('userData', JSON.stringify(res.data.user));
          localStorage.setItem('userToken', res.data.token);
          window.dispatchEvent(new Event('storage'));

          setTimeout(() => {
            navigate('/account');
          }, 1200);

          return;
        }

        setTimeout(() => {
          navigate('/login');
        }, 1200);

        return;
      }

      setError(res.data.error || 'Código incorrecto.');
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Error al verificar código.'
      );
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendDisabled(true);
    setResendTimer(60);
    setError('');

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendDisabled(false);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    try {
      const res = await axios.post(
        `${API_URL}/enviar_codigo.php`,
        {
          user_id: verificationUserId,
          email: verificationEmail
        },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (res.data && res.data.error) {
        setError(res.data.error);
      }
    } catch (err) {
      console.error(err);
      setError('No se pudo reenviar el código.');
    }
  };

  const renderGoogleArea = () => {
    if (showVerification) {
      return (
        <div className="register-google-box">
          <div className="register-google-verify-head">
            <div className="register-google-verify-icon">
              <FaCheckCircle />
            </div>

            <h3>Verifica tu email</h3>

            <p>
              Hemos enviado un código a <strong>{verificationEmail}</strong>.
              Revisa tu bandeja de entrada y spam.
            </p>
          </div>

          {verificationSuccess ? (
            <div className="register-google-success">
              <FaCheckCircle />
              <h3>¡Email verificado!</h3>
              <p>Redirigiendo a tu cuenta...</p>
            </div>
          ) : (
            <>
              {error && <div className="register-google-error">{error}</div>}

              <input
                type="text"
                maxLength="6"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6));
                  setError('');
                }}
                className="register-google-code-input"
                autoFocus
              />

              <button
                type="button"
                className="register-google-main-btn"
                onClick={handleVerifyCode}
                disabled={verificationLoading || verificationCode.length !== 6}
              >
                {verificationLoading ? 'Verificando...' : 'Verificar código'}
              </button>

              <div className="register-google-actions">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendDisabled}
                >
                  {resendDisabled ? `Reenviar en ${resendTimer}s` : 'Reenviar código'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowVerification(false);
                    setVerificationCode('');
                    setVerificationEmail('');
                    setVerificationUserId(null);
                    setVerificationSuccess(false);
                    setError('');
                  }}
                >
                  <FaArrowLeft /> Volver
                </button>
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <div className="register-google-box">
        <div className="register-google-divider">
          <span></span>
          <small>o regístrate con Google</small>
          <span></span>
        </div>

        {error && <div className="register-google-error">{error}</div>}

        <div className="register-google-login-shell">
          {googleLoading && (
            <div className="register-google-loading">
              Procesando Google...
            </div>
          )}

          <GoogleLogin
            onSuccess={handleGoogleRegisterSuccess}
            onError={() => setError('Error con el registro de Google.')}
            width="320"
            size="large"
            shape="pill"
            text="signup_with"
            theme="outline"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="register-futuristic-page">
      <style>
        {`
          .register-google-box {
            width: 100%;
            margin-top: 18px;
          }

          .register-google-divider {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            margin: 18px 0 14px;
            color: rgba(255,255,255,.78);
            font-size: 13px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: .04em;
          }

          .register-google-divider span {
            height: 1px;
            flex: 1;
            background: linear-gradient(90deg, transparent, rgba(255,204,0,.75), transparent);
          }

          .register-google-login-shell {
            position: relative;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 10px 6px 6px;
            border-radius: 18px;
            border: 1px solid rgba(255,204,0,.28);
            background:
              radial-gradient(circle at 20% 20%, rgba(255,204,0,.12), transparent 32%),
              linear-gradient(135deg, rgba(255,204,0,.08), rgba(255,255,255,.03));
          }

          .register-google-loading {
            position: absolute;
            inset: 0;
            z-index: 5;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(0,0,0,.75);
            color: #ffcc00;
            font-weight: 900;
            border-radius: 18px;
            backdrop-filter: blur(8px);
          }

          .register-google-error {
            width: 100%;
            margin: 12px 0;
            padding: 12px 14px;
            border-radius: 14px;
            border: 1px solid rgba(255,80,80,.55);
            background: rgba(255,80,80,.12);
            color: #ffb5b5;
            font-size: 14px;
            font-weight: 700;
            text-align: center;
          }

          .register-google-verify-head {
            text-align: center;
            color: #fff;
            padding: 18px 16px;
            border-radius: 20px;
            border: 1px solid rgba(255,204,0,.32);
            background: rgba(0,0,0,.32);
          }

          .register-google-verify-icon {
            width: 52px;
            height: 52px;
            margin: 0 auto 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 999px;
            background: linear-gradient(135deg, #ffcc00, #ff7a00);
            color: #000;
            font-size: 24px;
          }

          .register-google-verify-head h3,
          .register-google-success h3 {
            margin: 0 0 8px;
            color: #ffcc00;
            font-size: 22px;
            font-weight: 900;
          }

          .register-google-verify-head p,
          .register-google-success p {
            margin: 0;
            color: rgba(255,255,255,.82);
            font-size: 14px;
            line-height: 1.5;
          }

          .register-google-code-input {
            width: 100%;
            height: 58px;
            margin: 14px 0;
            border-radius: 16px;
            border: 1px solid rgba(255,204,0,.55);
            background: rgba(0,0,0,.72);
            color: #ffcc00;
            text-align: center;
            font-size: 28px;
            font-weight: 900;
            letter-spacing: 10px;
            outline: none;
          }

          .register-google-main-btn {
            width: 100%;
            min-height: 52px;
            border: none;
            border-radius: 999px;
            background: linear-gradient(135deg, #ffcc00, #ff7a00);
            color: #000;
            font-size: 15px;
            font-weight: 900;
            cursor: pointer;
          }

          .register-google-main-btn:disabled {
            opacity: .6;
            cursor: not-allowed;
          }

          .register-google-actions {
            display: flex;
            gap: 10px;
            margin-top: 12px;
          }

          .register-google-actions button {
            flex: 1;
            min-height: 44px;
            border-radius: 999px;
            border: 1px solid rgba(255,204,0,.38);
            background: rgba(0,0,0,.35);
            color: #fff;
            font-size: 13px;
            font-weight: 800;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
          }

          .register-google-actions button:disabled {
            opacity: .6;
            cursor: not-allowed;
          }

          .register-google-success {
            text-align: center;
            color: #fff;
            padding: 18px 0;
          }

          .register-google-success svg {
            font-size: 42px;
            color: #ffcc00;
            margin-bottom: 10px;
          }

          @media (max-width: 480px) {
            .register-google-login-shell {
              overflow: hidden;
            }

            .register-google-actions {
              flex-direction: column;
            }

            .register-google-code-input {
              font-size: 24px;
              letter-spacing: 7px;
            }
          }
        `}
      </style>

      <div className="register-energy-background">
        <div className="register-particle-field"></div>
        <div className="register-grid-overlay"></div>
        <div className="register-ambient-glow register-ambient-glow-one"></div>
        <div className="register-ambient-glow register-ambient-glow-two"></div>
      </div>

      <div className="register-electric-layer">
        <span className="register-electric-line"></span>
        <span className="register-electric-line"></span>
        <span className="register-electric-line"></span>
        <span className="register-electric-line"></span>
        <span className="register-electric-node"></span>
        <span className="register-electric-node"></span>
        <span className="register-electric-node"></span>
        <span className="register-electric-node"></span>
      </div>

      <main className="register-futuristic-shell">
        <section className="register-product-zone">
          <div className="register-product-scene">
            <div className="register-scene-orbit register-scene-orbit-one"></div>
            <div className="register-scene-orbit register-scene-orbit-two"></div>
            <div className="register-can-light"></div>

            <div className="register-can-wrapper">
              <img
                src={reanimeitorImage}
                alt="Lata de Reanimeitor Energy Drink"
                className="register-can-image"
              />
            </div>

            <div className="register-floating-chip register-floating-chip-one">
              <FaBolt />
              <span>Energía para tu día</span>
            </div>

            <div className="register-floating-chip register-floating-chip-two">
              <FaStar />
              <span>Packs disponibles</span>
            </div>

            <div className="register-floating-chip register-floating-chip-three">
              <FaCheckCircle />
              <span>Área de cliente</span>
            </div>
          </div>

          <div className="register-product-copy">
            <div className="register-kicker">
              <span></span>
              REANIMEITOR ENERGY DRINK
            </div>

            <h1>
              Crea tu cuenta
              <br />
              y activa tu energía.
            </h1>

            <p>
              Regístrate para comprar tus packs de Reanimeitor, consultar pedidos
              y gestionar tus datos desde tu área de cliente.
            </p>

            <div className="register-mini-stats register-desktop-stats">
              <div>
                <strong>Pedidos</strong>
                <small>consulta tus compras</small>
              </div>

              <div>
                <strong>Perfil</strong>
                <small>gestiona tus datos</small>
              </div>

              <div>
                <strong>Envíos</strong>
                <small>seguimiento y estado</small>
              </div>
            </div>

            <div className="register-mobile-stats-wrap">
              <div className="register-mobile-stats" ref={mobileStatsRef}>
                <div className="register-mobile-stat-slide">
                  <FaShoppingCart />
                  <strong>Pedidos</strong>
                  <small>consulta tus compras</small>
                </div>

                <div className="register-mobile-stat-slide">
                  <FaUserCircle />
                  <strong>Perfil</strong>
                  <small>gestiona tus datos</small>
                </div>

                <div className="register-mobile-stat-slide">
                  <FaShippingFast />
                  <strong>Envíos</strong>
                  <small>seguimiento y estado</small>
                </div>
              </div>

              <div className="register-mobile-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </section>

        <section className="register-access-zone">
          <div className="register-access-card">
            <div className="register-card-glow"></div>

            <div className="register-card-header">
              <div className="register-brand-mark">
                <FaUserPlus />
              </div>

              <div>
                <span className="register-brand-label">NUEVA CUENTA</span>
                <h2>Regístrate</h2>
              </div>
            </div>

            <p className="register-card-subtitle">
              Crea tu acceso para comprar, revisar tus pedidos y gestionar tu perfil.
            </p>

            <div className="register-form-frame">
              {!showVerification && <RegisterForm />}
              {renderGoogleArea()}
            </div>

            <div className="register-action-hint">
              <FaArrowRight />
              <span>Después de registrarte podrás acceder a tu área de cliente.</span>
            </div>

            <div className="register-features-grid">
              <div className="register-feature-card">
                <FaShoppingCart />
                <span>Compras</span>
              </div>

              <div className="register-feature-card">
                <FaUserPlus />
                <span>Cuenta</span>
              </div>

              <div className="register-feature-card">
                <FaShippingFast />
                <span>Pedidos</span>
              </div>

              <div className="register-feature-card">
                <FaShieldAlt />
                <span>Acceso seguro</span>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RegisterPage;